"use client";

import { useCallback, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { Mail } from "lucide-react";
import { Link } from "@/i18n/navigation";
import { track } from "@/components/analytics/track";
import { effectiveEmailSchema } from "@/lib/validation/email";

type Variant = "hero" | "gate";

export function SubscribeForm({
  variant,
  previewPercent,
  nextPath,
}: {
  variant: Variant;
  previewPercent?: number;
  nextPath?: string;
}) {
  const t = useTranslations("Research");
  const locale = useLocale();
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [formError, setFormError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [already, setAlready] = useState(false);

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setFormError("");
      const parsed = effectiveEmailSchema.safeParse(email);
      if (!parsed.success) {
        setEmailError(
          parsed.error.issues[0]?.message ?? t("errorEmail")
        );
        return;
      }
      setEmailError("");
      if (!consent) {
        setFormError(t("errorConsent"));
        return;
      }

      setBusy(true);
      try {
        const res = await fetch("/api/research/subscribe", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: parsed.data,
            locale,
            ...(nextPath ? { next: nextPath } : {}),
          }),
        });
        const data = (await res.json()) as {
          error?: string;
          alreadySubscribed?: boolean;
        };
        if (!res.ok) {
          setFormError(data.error ?? t("errorGeneric"));
          return;
        }
        track("research_subscribe", {
          meta: { variant, already: Boolean(data.alreadySubscribed) },
        });
        setAlready(Boolean(data.alreadySubscribed));
        setDone(true);
        if (variant === "gate") {
          window.location.reload();
        }
      } catch {
        setFormError(t("errorGeneric"));
      } finally {
        setBusy(false);
      }
    },
    [consent, email, locale, nextPath, t, variant]
  );

  if (done && variant !== "gate") {
    return (
      <p className="text-teal-light text-sm font-medium">
        {already ? t("subscribeAlready") : t("subscribeSuccess")}
      </p>
    );
  }

  return (
    <form onSubmit={(e) => void onSubmit(e)} className="space-y-4" id="subscribe">
      {variant === "gate" && previewPercent !== undefined ? (
        <p className="text-sm text-amber-200/90">
          {t("remainingHint", { percent: Math.max(0, 100 - previewPercent) })}
        </p>
      ) : null}

      <div>
        <label htmlFor={`research-email-${variant}`} className="sr-only">
          {t("emailLabel")}
        </label>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            id={`research-email-${variant}`}
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("emailPlaceholder")}
            className="flex-1 rounded-lg border border-slate-700 bg-slate-950/80 px-4 py-3 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-light"
          />
          <button
            type="submit"
            disabled={busy}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-teal-light px-6 py-3 text-sm font-bold text-slate-950 shadow-[0_0_24px_rgba(45,138,110,0.45)] hover:bg-[#3aa37f] transition-colors disabled:opacity-60"
          >
            <Mail className="h-4 w-4" aria-hidden />
            {busy ? t("subscribeCtaBusy") : t("subscribeCta")}
          </button>
        </div>
        {emailError ? (
          <p className="mt-2 text-sm text-maroon">{emailError}</p>
        ) : null}
      </div>

      <label className="flex items-start gap-2 text-sm text-slate-400">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-1"
        />
        <span>
          {t.rich("consent", {
            privacy: (chunks) => (
              <Link href="/privacy" className="text-teal-light underline">
                {chunks}
              </Link>
            ),
          })}
        </span>
      </label>
      {formError ? <p className="text-sm text-maroon">{formError}</p> : null}
    </form>
  );
}
